import React, { useState } from 'react';
import { CountLabel, GradientText, GradientTextAction, Poppins, Spacer, TableSkeleton } from 'src/common';
import { TabWrap } from 'src/components/TabsMenu';
import styled from 'styled-components';
import GenericTable from 'src/components/GenericTable';
import { useMembersColumns } from './useMembersColumns';
import { useInvitesColumns } from './useInvitesColumns';
import { TableIds } from 'src/api/types/misc';
import { useOrganisationMembersQuery } from 'src/graphql/_generated';
import { useRequestsColumns } from './useRequestsColumns';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { AddUserModal, UserModal } from 'src/components/modals';
import { isQueryLoading, useCache } from 'src/graphql';

const Div = styled(TabWrap)`
  background-color: ${({ theme }) => theme.colors.white};

  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .label {
    color: ${({ theme }) => theme.colors.cflowerBlue};
    font-size: 22px;
    &__total {
      color: ${({ theme }) => theme.colors.prussianBlue};
      font-size: 24px;
      font-weight: 600;
    }
  }

  .header-label {
    font-weight: bold;
    font-size: 18px;
    color: ${({ theme }) => theme.colors.cflowerBlue};
  }

  .separator {
    height: 1px;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.stroke};
  }
`;

interface MembersProps {
  organisationId: string;
}

export const Members: React.FC<MembersProps> = ({ organisationId }) => {
  const [addUserModalOpen, setAddUserModalOpen] = useState<{ name: string; email: string } | null>(null);
  const [profileModalId, setProfileModalId] = useState<string | null>(null);
  const { refetchQuery } = useCache();

  const { data, networkStatus, loading } = useOrganisationMembersQuery({ variables: { id: organisationId } });

  const isLoading = isQueryLoading({ data, networkStatus, loading });
  const organisation = data?.organisation;

  const { columns, GTColumnSelectAnchorExported } = useMembersColumns({
    data: organisation?.users,
    orgId: organisation?.id ?? '',
    setProfileModalId,
  });

  const { columns: columnsRequest, GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedRequest } =
    useRequestsColumns({ organisation: organisation || undefined });

  const { columns: columnsInvite, GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedInvite } =
    useInvitesColumns({ organisation: organisation || undefined });

  if (isLoading) {
    return (
      <Div>
        <TableSkeleton />
      </Div>
    );
  }

  if (!organisation) return null;

  return (
    <>
      {profileModalId && (
        <UserModal
          onClose={() => setProfileModalId(null)}
          userEmail={profileModalId}
          orgId={organisation.id}
          onUserUpdated={() => refetchQuery({ include: ['organisationMembers'] })}
          highlightRoles
        />
      )}
      {addUserModalOpen && (
        <AddUserModal
          id={organisation.id}
          type="organisation"
          onClose={() => setAddUserModalOpen(null)}
          emailsInUse={[...organisation.invites, ...organisation.requests, ...organisation.users].map(
            (el) => el.email || '',
          )}
          initialValues={addUserModalOpen}
        />
      )}
      <Div>
        {/* MEMBERS */}
        <GradientText>MEMBERS</GradientText>
        <Spacer $px={30} />
        <div className="head">
          <CountLabel label="member" length={organisation.users.length} />
          <GradientTextAction
            onClick={() => {
              setAddUserModalOpen({ name: '', email: '' });
              mpEvent(MPEvents.ButtonClick, {
                button: 'Add a user',
                tags: ['ORGANISATION'],
              });
            }}
            weight={500}
          >
            + Add a User
          </GradientTextAction>
        </div>
        <Spacer $px={20} />
        <GenericTable
          tableId={TableIds.orgMembers}
          data={organisation.users}
          columns={columns}
          GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
          searchable={['name', 'email', 'role']}
        />
        {/* REQUESTS */}
        {organisation.requests.length > 0 && organisation.actionsOnThisOrganisation?.canSeeRequests && (
          <>
            <Spacer $px={30} />
            <Poppins className="header-label">REQUESTS</Poppins>
            <Spacer $px={16} />
            <CountLabel label="request" length={organisation.requests.length} />
            <Spacer $px={5} />
            <GenericTable
              tableId={TableIds.orgRequests}
              data={organisation.requests}
              columns={columnsRequest}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedRequest}
              searchable={['email', 'name']}
            />
          </>
        )}
        {/* INVITES */}
        {organisation.invites.length > 0 && organisation.actionsOnThisOrganisation?.canSeeInvites && (
          <>
            <Spacer $px={30} />
            <Poppins className="header-label">INVITES</Poppins>
            <Spacer $px={16} />
            <CountLabel label="invite" length={organisation.invites.length} />
            <Spacer $px={5} />
            <GenericTable
              tableId={TableIds.orgInvites}
              data={organisation.invites}
              columns={columnsInvite}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedInvite}
              searchable={['email', 'name']}
            />
          </>
        )}
      </Div>
    </>
  );
};
